import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminHeader from '@/components/admin/AdminHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Save, AlertTriangle, CheckCircle, Search } from 'lucide-react';

interface Page {
  id: string;
  title: string;
  slug: string;
  seo_metadata: {
    id: string;
    meta_title: string | null;
    meta_description: string | null;
    canonical_url: string | null;
    indexable: boolean;
    og_title: string | null;
    og_description: string | null;
    og_image: string | null;
  } | null;
}

const SEO = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const selectedPageId = searchParams.get('page');

  const [formData, setFormData] = useState({
    meta_title: '',
    meta_description: '',
    canonical_url: '',
    indexable: true,
    og_title: '',
    og_description: '',
    og_image: '',
  });

  // Fetch all pages with their SEO metadata
  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages-seo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pages')
        .select(`
          id,
          title,
          slug,
          seo_metadata (
            id,
            meta_title,
            meta_description,
            canonical_url,
            indexable,
            og_title,
            og_description,
            og_image
          )
        `)
        .order('title');

      if (error) throw error;
      return data as Page[];
    },
  });

  // Load SEO data when page is selected
  const selectedPage = pages?.find(p => p.id === selectedPageId);

  const loadPageSEO = (page: Page) => {
    setSearchParams({ page: page.id });
    if (page.seo_metadata) {
      setFormData({
        meta_title: page.seo_metadata.meta_title || '',
        meta_description: page.seo_metadata.meta_description || '',
        canonical_url: page.seo_metadata.canonical_url || '',
        indexable: page.seo_metadata.indexable,
        og_title: page.seo_metadata.og_title || '',
        og_description: page.seo_metadata.og_description || '',
        og_image: page.seo_metadata.og_image || '',
      });
    } else {
      setFormData({
        meta_title: page.title,
        meta_description: '',
        canonical_url: `/${page.slug}`,
        indexable: true,
        og_title: page.title,
        og_description: '',
        og_image: '',
      });
    }
  };

  // Save SEO mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPageId) throw new Error('No page selected');

      const seoData = {
        page_id: selectedPageId,
        meta_title: formData.meta_title || null,
        meta_description: formData.meta_description || null,
        canonical_url: formData.canonical_url || null,
        indexable: formData.indexable,
        og_title: formData.og_title || null,
        og_description: formData.og_description || null,
        og_image: formData.og_image || null,
      };

      // Check if SEO metadata exists
      const { data: existing } = await supabase
        .from('seo_metadata')
        .select('id')
        .eq('page_id', selectedPageId)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('seo_metadata')
          .update(seoData)
          .eq('page_id', selectedPageId);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('seo_metadata')
          .insert(seoData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('SEO settings saved successfully');
      queryClient.invalidateQueries({ queryKey: ['pages-seo'] });
      queryClient.invalidateQueries({ queryKey: ['seo-issues'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error) => {
      console.error('Error saving SEO:', error);
      toast.error('Failed to save SEO settings');
    },
  });

  const getSEOStatus = (page: Page) => {
    const seo = page.seo_metadata;
    if (!seo) return { status: 'error', message: 'No SEO configured' };
    
    const issues = [];
    if (!seo.meta_title) issues.push('Missing title');
    if (seo.meta_title && seo.meta_title.length > 60) issues.push('Title too long');
    if (!seo.meta_description) issues.push('Missing description');
    if (!seo.canonical_url) issues.push('No canonical URL');

    if (issues.length === 0) return { status: 'good', message: 'All good' };
    if (issues.length <= 2) return { status: 'warning', message: issues.join(', ') };
    return { status: 'error', message: `${issues.length} issues` };
  };

  return (
    <div className="min-h-screen">
      <AdminHeader 
        title="SEO Settings" 
        description="Manage meta tags and search engine optimization for your pages"
      />
      
      <main className="p-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Page List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Pages</CardTitle>
              <CardDescription>Select a page to edit its SEO</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : pages && pages.length > 0 ? (
                <div className="space-y-2">
                  {pages.map((page) => {
                    const seoStatus = getSEOStatus(page);
                    return (
                      <button
                        key={page.id}
                        onClick={() => loadPageSEO(page)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedPageId === page.id
                            ? 'bg-primary/10 border-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{page.title}</span>
                          {seoStatus.status === 'good' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : seoStatus.status === 'warning' ? (
                            <AlertTriangle className="h-4 w-4 text-orange-500" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          /{page.slug}
                        </p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No pages found
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEO Editor */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedPage ? `SEO for: ${selectedPage.title}` : 'Select a Page'}
              </CardTitle>
              <CardDescription>
                {selectedPage 
                  ? 'Configure meta tags and Open Graph settings'
                  : 'Choose a page from the list to edit its SEO settings'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedPage ? (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="meta_title">
                      Meta Title
                      <span className={`ml-2 text-xs ${
                        formData.meta_title.length > 60 
                          ? 'text-orange-500' 
                          : 'text-muted-foreground'
                      }`}>
                        ({formData.meta_title.length}/60)
                      </span>
                    </Label>
                    <Input
                      id="meta_title"
                      value={formData.meta_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                      placeholder="Page title for search engines"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meta_description">
                      Meta Description
                      <span className={`ml-2 text-xs ${
                        formData.meta_description.length > 160 
                          ? 'text-orange-500' 
                          : 'text-muted-foreground'
                      }`}>
                        ({formData.meta_description.length}/160)
                      </span>
                    </Label>
                    <Textarea
                      id="meta_description"
                      value={formData.meta_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                      placeholder="Brief description for search results"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="canonical_url">Canonical URL</Label>
                    <Input
                      id="canonical_url"
                      value={formData.canonical_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, canonical_url: e.target.value }))}
                      placeholder="/page-slug"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="indexable">Allow Search Indexing</Label>
                      <p className="text-xs text-muted-foreground">
                        Allow search engines to index this page
                      </p>
                    </div>
                    <Switch
                      id="indexable"
                      checked={formData.indexable}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, indexable: checked }))}
                    />
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium mb-4">Open Graph (Social Sharing)</h3>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="og_title">OG Title</Label>
                        <Input
                          id="og_title"
                          value={formData.og_title}
                          onChange={(e) => setFormData(prev => ({ ...prev, og_title: e.target.value }))}
                          placeholder="Title for social sharing"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="og_description">OG Description</Label>
                        <Textarea
                          id="og_description"
                          value={formData.og_description}
                          onChange={(e) => setFormData(prev => ({ ...prev, og_description: e.target.value }))}
                          placeholder="Description for social sharing"
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="og_image">OG Image URL</Label>
                        <Input
                          id="og_image"
                          value={formData.og_image}
                          onChange={(e) => setFormData(prev => ({ ...prev, og_image: e.target.value }))}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saveMutation.isPending ? 'Saving...' : 'Save SEO Settings'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Select a page from the list to manage its SEO settings
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SEO;
